// apps/merchant-v3/src/components/ProfileDropdown.tsx
//
// Phase 6 Wave 1 Task 02 — TopBar profile dropdown. Avatar
// (or initials), name, loyalty tier badge (the pricing-tier rung, e.g.
// /229), menu items: Profile, Logout. Logout is a
// stub here — real /api/v1/auth/logout call lands Task 03.
//
// vg-binding: PLAN:task-02
// vg-binding: P6.D-05

import { useState } from 'react';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@printway/ui';
import { Link, useNavigate } from 'react-router-dom';
import { LogOut, User as UserIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useUser } from '@/store/user';
import { useAuth } from '@/lib/auth/AuthProvider';
import { AvatarImage } from '@/components/AvatarImage';
import { TierBadge } from '@/components/TierBadge';

// dbg-20260929-loyaltytier — `tierClasses` moved to `TierBadge`, which is
// now the only description of this badge. It used to live here while three
// other surfaces drew their own with a flat `bg-brand-50`, so one tier had two
// colours depending on where you looked at it.

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() ?? '').join('') || '?';
}

export function ProfileDropdown(): JSX.Element {
  const { t } = useTranslation('common');
  const user = useUser();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [loggingOut, setLoggingOut] = useState(false);

  const name = user?.name ?? t('profile.guest', { defaultValue: 'Guest' });
  // dbg-20260929-loyaltytier — no badge rather than a guessed one.
  const tier = user?.loyaltyTier ?? null;

  const handleLogout = (): void => {
    if (loggingOut) return;
    setLoggingOut(true);
    // POST /auth/logout → clear cookie + cache + localStorage (AuthProvider
    // owns the full revoke). Cookie clear is best-effort so we navigate
    // regardless of outcome — useAuth().logout already swallows 401.
    void logout()
      .catch(() => {
        // network failure: server cookie may persist but local state is
        // hard-cleared in logoutMutation.onSettled — UI is consistent.
      })
      .finally(() => {
        navigate('/login', { replace: true });
      });
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          data-testid="topbar-profile-dropdown"
          aria-label={t('a11y.openProfileMenu')}
          title={name}
          className="inline-flex min-w-0 max-w-[200px] shrink-0 items-center gap-2 rounded-full px-1.5 py-0.5 hover:bg-pw-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
        >
          <AvatarImage
            src={user?.avatarUrl}
            alt={name}
            className="h-8 w-8 shrink-0 rounded-full bg-brand-100 text-sm font-semibold text-brand-700"
            fallback={<span aria-hidden>{getInitials(name)}</span>}
          />
          <span className="hidden min-w-0 max-w-[120px] flex-col items-start leading-tight md:flex">
            <span
              className="block w-full truncate text-sm font-medium text-pw-text"
              title={name}
            >
              {name}
            </span>
            <TierBadge tier={tier} testId="profile-loyalty-tier" />
          </span>
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="end"
        sideOffset={6}
        className="z-50 min-w-[200px] rounded-md border border-pw-border bg-pw-card p-1 shadow-md"
      >
        <div className="px-2 py-1.5">
          <div className="text-sm font-medium text-pw-text">{name}</div>
          <div className="text-xs text-pw-text-muted">
            {user?.email ?? t('profile.notSignedIn', { defaultValue: 'Not signed in' })}
          </div>
        </div>
        <DropdownMenuSeparator className="my-1 h-px bg-pw-surface" />
        <DropdownMenuItem asChild>
          <Link
            to="/settings/profile"
            className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm text-pw-text-muted outline-none focus:bg-pw-surface data-[highlighted]:bg-pw-surface"
          >
            <UserIcon className="h-4 w-4" aria-hidden />
            {t('navigation.sidebar.profile')}
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator className="my-1 h-px bg-pw-surface" />
        <DropdownMenuItem
          onSelect={handleLogout}
          className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm text-red-600 outline-none focus:bg-red-50 data-[highlighted]:bg-red-50"
        >
          <LogOut className="h-4 w-4" aria-hidden />
          {t('actions.logout')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default ProfileDropdown;
